import { z } from 'zod'
import {
  CRM_WORK_READ_CONTRACT_VERSION,
  crmEnrichmentSummarySchema,
} from './crm-work-read'

/**
 * Request schemas intentionally preserve the handwritten validator boundary.
 * Domain validation, aliases, and exact 400 messages remain in the CRM
 * handlers; these schemas describe the transport accepted by clients/tests.
 */
const optionalUnknown = z.unknown().optional()

export const crmEnrichmentDecisionRequestSchema = z.object({
  decision: optionalUnknown,
}).passthrough()

export const crmEnrichmentBatchApplyRequestSchema = z.object({
  proposalIds: optionalUnknown,
  proposal_ids: optionalUnknown,
}).passthrough()

export const crmEnrichmentWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.boolean(),
}).passthrough()

export const crmEnrichmentApplyOutcomeSchema = z.object({
  companyPatched: z.boolean(),
  contactPatched: z.boolean(),
  tasksCreated: z.number().int().nonnegative(),
  taskIds: z.array(z.string()),
}).passthrough()

export const crmEnrichmentDecisionResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  proposal: crmEnrichmentSummarySchema,
  changed: z.boolean(),
  sideEffects: crmEnrichmentWriteSideEffectsSchema,
}).passthrough()

export const crmEnrichmentApplyResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  proposal: crmEnrichmentSummarySchema,
  applied: z.boolean(),
  replayed: z.boolean(),
  result: crmEnrichmentApplyOutcomeSchema,
  sideEffects: crmEnrichmentWriteSideEffectsSchema,
}).passthrough()

export const crmEnrichmentBatchApplyRowSchema = z.object({
  proposalId: z.string(),
  status: z.enum(['applied', 'replayed', 'failed']),
  errorCode: z.string().optional(),
  result: crmEnrichmentApplyOutcomeSchema.optional(),
}).passthrough()

export const crmEnrichmentBatchApplyResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  requested: z.number().int().nonnegative(),
  applied: z.number().int().nonnegative(),
  replayed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  results: z.array(crmEnrichmentBatchApplyRowSchema),
  sideEffects: crmEnrichmentWriteSideEffectsSchema,
}).passthrough()

export const CRM_ENRICHMENT_WRITE_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_ENRICHMENT_APPLY_GATE_CLOSED',
] as const

export const crmEnrichmentWriteErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_ENRICHMENT_WRITE_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
  }).passthrough(),
  gate: z.object({
    open: z.literal(false),
    reason: z.string(),
  }).passthrough().optional(),
}).passthrough()

export type CrmEnrichmentDecisionRequest = z.infer<typeof crmEnrichmentDecisionRequestSchema>
export type CrmEnrichmentBatchApplyRequest = z.infer<typeof crmEnrichmentBatchApplyRequestSchema>
export type CrmEnrichmentWriteSideEffects = z.infer<typeof crmEnrichmentWriteSideEffectsSchema>
export type CrmEnrichmentApplyOutcome = z.infer<typeof crmEnrichmentApplyOutcomeSchema>
export type CrmEnrichmentDecisionResponse = z.infer<typeof crmEnrichmentDecisionResponseSchema>
export type CrmEnrichmentApplyResponse = z.infer<typeof crmEnrichmentApplyResponseSchema>
export type CrmEnrichmentBatchApplyRow = z.infer<typeof crmEnrichmentBatchApplyRowSchema>
export type CrmEnrichmentBatchApplyResponse = z.infer<typeof crmEnrichmentBatchApplyResponseSchema>
export type CrmEnrichmentWriteErrorCode = (typeof CRM_ENRICHMENT_WRITE_ERROR_CODES)[number]
export type CrmEnrichmentWriteErrorEnvelope = z.infer<typeof crmEnrichmentWriteErrorEnvelopeSchema>
